import { z } from "zod";

/**
 * Per-section booking colours.
 *
 * The public booking flow derives its whole look from a single brand colour
 * (`brandColor` / `darkBrandColor`). `bookingTheme` lets a user override colours
 * *per section* of the booking flow (slots, calendar, confirmation, success).
 *
 * Everything here is optional: any section/colour that is not set falls back to
 * the brand colour via CSS variables, so an empty/absent `bookingTheme` keeps the
 * exact same appearance as before.
 */

// Local hex validation kept dependency-light so this schema can run server-side
// without pulling in the client-only helpers from `getBrandColours.tsx`.
// Mirrors `isValidHexCode` (#rgb or #rrggbb).
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}){1,2}$/;

export const zHexColor = z.string().regex(HEX_COLOR_PATTERN, { message: "Invalid hex colour" });

/** A light/dark pair of hex colours. Either side may be omitted (inherits brand). */
export const ZBookingColorPair = z
  .object({
    light: zHexColor.optional(),
    dark: zHexColor.optional(),
  })
  .strict();

/**
 * Hybrid model: one accent + one key text override per section.
 * `bg` is an optional background colour (currently used by the time-slot buttons).
 */
export const ZBookingSection = z
  .object({
    accent: ZBookingColorPair.optional(),
    text: ZBookingColorPair.optional(),
    bg: ZBookingColorPair.optional(),
  })
  .strict();

/**
 * Public profile page (the event-type listing at `/[user]`). Unlike the booking
 * sections above, these are direct background colours (no derivation), each a
 * light/dark pair. When unset, they fall back to the existing neutral tokens.
 */
export const ZProfileSection = z
  .object({
    nameBg: ZBookingColorPair.optional(),
    cardBg: ZBookingColorPair.optional(),
    cardHover: ZBookingColorPair.optional(),
    durationBg: ZBookingColorPair.optional(),
    /**
     * Fond de la pilule de filtre ACTIVE. Couleur indépendante (elle ne dérive d'aucune autre
     * valeur configurée), donc exposée au praticien. Son texte, lui, est dérivé par contraste.
     */
    pillActive: ZBookingColorPair.optional(),
  })
  .strict();

export const BOOKING_THEME_VERSION = 1 as const;

export const ZBookingThemeSchema = z
  .object({
    v: z.literal(BOOKING_THEME_VERSION).optional(),
    slots: ZBookingSection.optional(),
    calendar: ZBookingSection.optional(),
    confirmation: ZBookingSection.optional(),
    success: ZBookingSection.optional(),
    profile: ZProfileSection.optional(),
    // Overall background of the whole booking widget (all 3 columns). Light/dark pair.
    background: ZBookingColorPair.optional(),
    /**
     * Fond de PAGE des vitrines publiques (derrière la carte de réservation et le profil).
     * Couleur indépendante — elle ne dérive d'aucun accent — donc exposée au praticien.
     */
    pageBg: ZBookingColorPair.optional(),
  })
  .strict();

export type BookingColorPair = z.infer<typeof ZBookingColorPair>;
export type BookingSection = z.infer<typeof ZBookingSection>;
export type ProfileSection = z.infer<typeof ZProfileSection>;
export type BookingTheme = z.infer<typeof ZBookingThemeSchema>;

/** The four "accent + text" booking sections, in display order. */
export const BOOKING_THEME_SECTIONS = ["slots", "calendar", "confirmation", "success"] as const;
export type BookingThemeSection = (typeof BOOKING_THEME_SECTIONS)[number];

/** Les fonds personnalisables de la page profil publique, dans l'ordre d'affichage. */
export const PROFILE_SECTION_FIELDS = ["nameBg", "cardBg", "cardHover", "durationBg", "pillActive"] as const;
export type ProfileSectionField = (typeof PROFILE_SECTION_FIELDS)[number];

/** Empty theme: every section inherits the brand colour. */
export const DEFAULT_BOOKING_THEME: BookingTheme = { v: BOOKING_THEME_VERSION };
